/**
 * Submitting a new ticket, once, and going straight to it.
 *
 * Two things worth knowing:
 *
 * **Idempotency.** `createTicket` requires an `Idempotency-Key`, and the
 * contract dedupes on "same key + same body within 24h". So the key is cached
 * against a fingerprint of the payload: pressing submit twice, or retrying
 * after a timeout, reuses the key and cannot produce a second ticket — while
 * editing the form and submitting again is a genuinely new attempt and gets a
 * fresh key rather than a 400 `IDEMPOTENCY_KEY_CONFLICT`. A caller whose body
 * carries server-derived values (the incident reporter) passes its own
 * fingerprint of what the person entered instead.
 *
 * **One at a time.** A synchronous in-flight guard drops a second call made
 * before the first settles — before `isPending` has even re-rendered.
 *
 * **Optimistic navigation.** The mutation's `onSuccess` seeds the returned
 * `TicketDetail` into the detail query cache before this navigates, so the
 * ticket page paints from real data immediately instead of flashing a loader.
 *
 * **Raised for someone else.** An incident filed for a colleague is theirs:
 * the reporter cannot open it and it is not in their My tickets. So instead of
 * navigating to it, `raisedFor` is set and the owner shows "Raised for <name>
 * — they'll get the updates. Ticket number …".
 *
 * **Back where it came from.** A form opened from an access request or a new
 * starter case passes `returnTo` (5 Oct, product owner's UAT: they raise the
 * IT Setup, 365FO, Bluestar, Power BI and CRM one after another from the case
 * page, and landing on each new ticket meant finding the case again). The
 * toast still names the new number; `onCreated` refreshes the case first.
 */

import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useT } from "@/i18n";
import { logger } from "@/lib/logger";

import type { TicketCreateRequest } from "../api/contract";
import { newIdempotencyKey } from "../api/operations";
import { useCreateTicket } from "../api/queries";
import { type OnBehalfPerson, raisedForName } from "./on-behalf";
import { useFeedbackToast } from "./use-feedback-toast";

/** What was raised for a colleague, for the confirmation. */
export interface RaisedFor {
  name: string;
  number: string;
}

/** Where to go after filing instead of the new ticket (a case page, 5 Oct). */
export interface ReturnAfterCreate {
  to: string;
  /** Run before navigating: refresh what the page shows (its queries). */
  onCreated?: () => void;
}

export interface UseTicketSubmitResult {
  /**
   * File it. `fingerprint` identifies the attempt for its idempotency key;
   * it defaults to the whole body.
   */
  submit: (
    body: TicketCreateRequest,
    fingerprint?: string,
    forPerson?: OnBehalfPerson | null,
  ) => Promise<void>;
  isPending: boolean;
  error: unknown;
  /** Set once an incident has been raised for a colleague; null otherwise. */
  raisedFor: RaisedFor | null;
  /** Clear `raisedFor`, to report something else. */
  reset: () => void;
}

export function useTicketSubmit(returnTo: ReturnAfterCreate | null = null): UseTicketSubmitResult {
  const { t } = useT("tickets");
  const navigate = useNavigate();
  const toast = useFeedbackToast();
  const mutation = useCreateTicket();
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const inFlight = useRef(false);
  const [raisedFor, setRaisedFor] = useState<RaisedFor | null>(null);

  const submit = useCallback(
    async (
      body: TicketCreateRequest,
      fingerprint: string = JSON.stringify(body),
      forPerson: OnBehalfPerson | null = null,
    ) => {
      if (inFlight.current) {
        logger.debug("[tickets] create already in flight; ignoring");
        return;
      }
      inFlight.current = true;
      if (attempt.current === null || attempt.current.fingerprint !== fingerprint) {
        attempt.current = { fingerprint, key: newIdempotencyKey() };
      }

      try {
        const detail = await mutation.mutateAsync({
          body,
          idempotencyKey: attempt.current.key,
        });
        // A fresh key for whatever the user raises next.
        attempt.current = null;
        if (forPerson !== null) {
          const name = raisedForName(detail.requester, forPerson) ?? forPerson.displayName;
          toast.success(t("onBehalf.raised", { name, number: detail.ticket_number }));
          setRaisedFor({ name, number: detail.ticket_number });
          return;
        }
        toast.success(t("create.success", { number: detail.ticket_number }));
        if (returnTo !== null) {
          returnTo.onCreated?.();
          await navigate(returnTo.to);
          return;
        }
        await navigate(
          detail.url_path.length > 0 ? detail.url_path : `/tickets/${detail.ticket_id}`,
        );
      } catch (error) {
        logger.debug("[tickets] create failed", error);
        toast.failure(error, t("create.failure"));
      } finally {
        inFlight.current = false;
      }
    },
    [mutation, navigate, returnTo, t, toast],
  );

  const reset = useCallback(() => {
    setRaisedFor(null);
    mutation.reset();
  }, [mutation]);

  return { submit, isPending: mutation.isPending, error: mutation.error, raisedFor, reset };
}
